import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, BellOff, Check, CheckCheck, Loader2, MailOpen, X } from "lucide-react";
import { Button, EmptyState, Skeleton, cx } from "../../../pages/admin/_shared";
import { BUCKET_ORDER, TYPE_META, dayBucket, fullTime, iconFor, iconToneFor, timeAgo } from "./notificationMeta";

function Item({ n, onOpen, onToggleRead, onDismiss, selectMode, selected, onSelect, compact }) {
  const Icon = iconFor(n);
  return (
    <li className="group relative">
      <div
        className={cx(
          "flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition",
          selected ? "bg-admin-primaryLight" : !n.read ? "bg-admin-primaryLight/40 hover:bg-admin-primaryLight/70" : "hover:bg-admin-muted"
        )}
      >
        {selectMode ? (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(n._id)}
            aria-label={`Chọn: ${n.title}`}
            className="mt-2.5 h-4 w-4 shrink-0 cursor-pointer rounded border-admin-border accent-[rgb(var(--admin-primary))]"
          />
        ) : null}

        <button type="button" onClick={() => (selectMode ? onSelect(n._id) : onOpen(n))} className="flex min-w-0 flex-1 items-start gap-3 text-left">
          <span className={cx("mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white shadow-sm", iconToneFor(n))}>
            <Icon size={18} strokeWidth={2.25} />
          </span>
          <span className="min-w-0 flex-1">
            <span className={cx("block text-sm text-admin-text", n.read ? "font-medium" : "font-semibold")}>{n.title}</span>
            {n.message ? (
              <span className={cx("mt-0.5 block text-xs leading-relaxed text-admin-textMuted", compact ? "line-clamp-2" : "line-clamp-3")}>{n.message}</span>
            ) : null}
            <span className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-admin-textMuted/80">
              <time dateTime={n.createdAt} title={fullTime(n.createdAt)}>
                {timeAgo(n.createdAt)}
              </time>
              {!compact && TYPE_META[n.type] ? <span className="rounded-full bg-admin-muted px-2 py-0.5 text-[10px] font-medium text-admin-textMuted">{TYPE_META[n.type].label}</span> : null}
            </span>
          </span>
        </button>

        {!selectMode ? (
          <div className="flex shrink-0 items-center gap-0.5">
            {/* Thao tác nhanh: hiện khi rê chuột / focus; chấm chưa đọc ẩn đi khi đó */}
            <div className="hidden items-center gap-0.5 group-focus-within:flex group-hover:flex">
              <button
                type="button"
                onClick={() => onToggleRead(n)}
                title={n.read ? "Đánh dấu chưa đọc" : "Đánh dấu đã đọc"}
                aria-label={n.read ? "Đánh dấu chưa đọc" : "Đánh dấu đã đọc"}
                className="flex h-7 w-7 items-center justify-center rounded-md text-admin-textMuted transition hover:bg-admin-surface hover:text-admin-text"
              >
                {n.read ? <MailOpen size={14} /> : <Check size={14} />}
              </button>
              <button
                type="button"
                onClick={() => onDismiss(n)}
                title="Ẩn thông báo"
                aria-label="Ẩn thông báo"
                className="flex h-7 w-7 items-center justify-center rounded-md text-admin-textMuted transition hover:bg-admin-surface hover:text-rose-600"
              >
                <X size={14} />
              </button>
            </div>
            {!n.read ? <span className="ml-1 mt-0.5 h-2 w-2 shrink-0 self-start rounded-full bg-admin-primary group-focus-within:hidden group-hover:hidden" aria-label="Chưa đọc" /> : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Danh sách thông báo dùng chung cho dropdown trên header và trang /admin/notifications.
 * `feed` là kết quả của useNotificationFeed.
 */
export default function NotificationList({
  feed,
  onNavigate,
  grouped = true,
  compact = false,
  selectMode = false,
  selected,
  onSelect,
  emptyHint,
  emptyAction,
}) {
  const navigate = useNavigate();
  const { items, loading, loadingMore, error, hasMore, reload, loadMore, markRead, markUnread, dismiss } = feed;

  const open = (n) => {
    markRead(n._id);
    if (n.link) {
      onNavigate?.();
      navigate(n.link);
    }
  };
  const toggleRead = (n) => (n.read ? markUnread(n._id) : markRead(n._id));

  if (loading && items.length === 0) {
    return (
      <div className="space-y-4 p-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Không tải được thông báo"
        description={error}
        action={
          <Button size="sm" variant="secondary" onClick={() => reload()}>
            Thử lại
          </Button>
        }
      />
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        icon={BellOff}
        title="Không có thông báo"
        description={emptyHint || "Khi có người dùng mới hoặc sự kiện quan trọng, bạn sẽ thấy ở đây."}
        action={emptyAction}
      />
    );
  }

  const groups = grouped
    ? items.reduce((acc, n) => {
        const k = dayBucket(n.createdAt);
        (acc[k] = acc[k] || []).push(n);
        return acc;
      }, {})
    : { "": items };
  const labels = grouped ? BUCKET_ORDER.filter((b) => groups[b]) : [""];

  return (
    <div className="p-1.5">
      {labels.map((label) => (
        <section key={label || "all"}>
          {label ? (
            <h3 className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-admin-textMuted">{label}</h3>
          ) : null}
          <ul className="space-y-0.5">
            {groups[label].map((n) => (
              <Item
                key={n._id}
                n={n}
                compact={compact}
                onOpen={open}
                onToggleRead={toggleRead}
                onDismiss={(x) => dismiss(x._id)}
                selectMode={selectMode}
                selected={Boolean(selected?.has(n._id))}
                onSelect={onSelect}
              />
            ))}
          </ul>
        </section>
      ))}
      {hasMore ? (
        <div className="p-2">
          <Button variant="secondary" size="sm" className="w-full" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? <Loader2 size={14} className="animate-spin" /> : null}
            Tải thêm
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function MarkAllReadButton({ feed, unread }) {
  return (
    <button
      type="button"
      onClick={feed.markAllRead}
      disabled={unread === 0}
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold text-admin-primary transition hover:bg-admin-primaryLight disabled:cursor-not-allowed disabled:text-admin-textMuted disabled:hover:bg-transparent"
    >
      <CheckCheck size={14} />
      Đọc hết
    </button>
  );
}
